import { Button } from '@ibsys/kit';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTablePresets } from '@/entities/settings';
import { PresetOfferBar } from './PresetOfferBar';

type Props = {
  /**
   * Ключ рабочей записи в `settings.tables`: имя таблицы (например, `'dialogsListV2'`) либо
   * ключ раздела — у таблицы с разделами активный пресет и вид свои у каждого из них.
   */
  sectionKey: string;
};

/**
 * Предложение сохранить донастроенный общий пресет — системный или совместный — личной копией
 * с отказом от донастройки.
 *
 * Показывается, пока пользователь держит донастроенным применённый общий пресет, которого
 * не вправе править (системный без разрешения на управление, совместный, которым с ним
 * поделились): записать в пресет донастройку ему нечем — распоряжаться он может только своей
 * копией. Копия сохраняется из текущего вида и сразу становится активной, поэтому предложение
 * исчезает само; отмена изменений — второй исход: вид возвращается к пресету.
 */
export const SystemPresetCopyOffer: FC<Props> = ({ sectionKey }) => {
  const { t } = useTranslation('tablePresets');
  const { activePreset, isDirty, savePresetAs, apply } = useTablePresets(sectionKey);

  const isCopyOffered =
    Boolean(activePreset?.isSystem || activePreset?.isShared) && !activePreset?.isEditable && isDirty;

  const handleSaveCopy = useCallback(() => {
    if (!activePreset) return;
    savePresetAs(t('systemPresetCopySuffix', { name: activePreset.name }));
  }, [activePreset, savePresetAs, t]);

  // Отказ от донастройки: применяем пресет заново — вид возвращается к нему, сам пресет
  // остаётся применённым, копия не создаётся, и предложение исчезает вместе с донастройкой.
  const handleUndoChanges = useCallback(() => {
    if (!activePreset) return;
    apply(activePreset.id);
  }, [activePreset, apply]);

  if (!isCopyOffered) return null;

  return (
    <PresetOfferBar hint={activePreset?.isShared ? t('sharedPresetCopyHint') : t('systemPresetCopyHint')}>
      <Button size="sm" variant="ghost" onClick={handleUndoChanges}>
        {t('undoChanges')}
      </Button>
      <Button size="sm" onClick={handleSaveCopy}>
        {t('systemPresetCopy')}
      </Button>
    </PresetOfferBar>
  );
};
